import React, {useContext, useEffect, useState} from "react";
import {Linking} from "react-native";
import P from "../lib/P";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import {useNavigation} from "@react-navigation/native";
import {StoreContext} from "./StoreContextProvider";
import {ActivePromoTypes, smgEnvironment, Version} from "../constants";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {captureUrlOverrides} from "../utils/devOverrides";
import {consumeExplicitPartnerSignOut} from "../utils/partnerSignOutGate";
import {clearPartnerRemovalGate, isRemovalGatedForSession} from "../utils/partnerRemovalGate";
import {AUTH_ERROR_MESSAGES, resolveAuthError} from "../auth/authErrorPolicy";

const PartnerAuth = () => {
  const [tokenId, setTokenID] = useState('');
  const [sessionID, setSessionID] = useState('');
  const navigation: NativeStackNavigationProp<any> = useNavigation();
  const Context = useContext(StoreContext);
  const {cancelModalConfig, session, user} = Context;

  // Post-login navigation. BSA cutover note: this used to call
  // partner-service.FullSwingCheckIn whose only effect was to insert a
  // partner.fs_station_session_participant row — irrelevant once
  // bay-session owns the session-participant projection. The bay-session
  // FullSwingAuth call inside Context.fullSwingTokenLogin already
  // implicit-joins the player. Keeping the navigation logic; dropping
  // the deprecated RPC.
  const proceedAfterLogin = (promoType: string) => {
    navigation.navigate(
      ActivePromoTypes.includes(promoType) ? 'tournaments' : 'challenges'
    );
  };

  const fullSwingTokenLogin = (session: string, token: string, promoType: string) => {
    Context.setLoadingOverlay(true);

    const s: string = session || (sessionID || '').trim();
    const t: string = token || (tokenId || '').trim();

    Context.fullSwingTokenLogin(s, t).then(user => {
      Context.setLoadingOverlay(false);
      // SMG-4069: any successful login drops the removal gate — either the
      // player deliberately rejoined this session, or they scanned into a
      // different one and the stale marker no longer describes reality.
      clearPartnerRemovalGate().catch(() => {});

      if (user.tncAccepted) {
        proceedAfterLogin(promoType);

      } else {
        navigation.navigate('Register');
      }

    }).catch(async error => {
      Context.setLoadingOverlay(false);
      const resolution = resolveAuthError(error, 'sign-in');
      // Defensive scrub: if any future regression in fullSwingTokenLogin
      // re-orders a side effect before a throw, this catch ensures we
      // do not leave a partial-auth bayPlayerJwt persisted on disk.
      // Today's implementation (StoreContextProvider commit per
      // SMG-3012 panel) already gates setBayPlayerJwt behind all
      // checks, but the panel review surfaced this class of bug as
      // worth defense-in-depth.
      try {
        await Context.setBayPlayerJwt('');

      } catch {
        // Credential storage reports sanitized telemetry. Keep surfacing the
        // original login failure, which is the actionable error for the user.
      }
      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle: resolution.userMessage, rightBtnText: 'OK',
        onResume: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false})
        }
      });
    });
  };

  useEffect(() => {
    // Capture URL overrides into the module-scope cache before
    // `navigation.navigate(...)` below drops the query string.
    captureUrlOverrides();
    // FSG-8815 reopen: an explicit sign-out must not be undone by this
    // auto-auth. On web, `Linking.getInitialURL()` returns the page's
    // ORIGINAL load URL for the life of the page (react-native-web caches
    // window.location.href at module load), so the post-logout
    // `navigation.reset` onto this screen re-recovers the QR deep-link
    // credentials and used to re-auth + re-join the player milliseconds
    // after they signed out. When the gate is armed (one-shot, armed by
    // logout()), drop the recovered credentials and fall through to the
    // no-credential path — which still hands the user back to the
    // FullSwing referrer when one is recorded.
    const explicitSignOut = consumeExplicitPartnerSignOut();
    Linking.getInitialURL().then((url) => {
      if (url) {
        let token = '';
        let sessionId = '';
        let promoType = '';

        try {
          // Use URL and URLSearchParams for robust parsing
          const parsedUrl = new URL(url);
          const params = new URLSearchParams(parsedUrl.search);

          token = params.get('id_token') || '';
          sessionId = params.get('session_id') || '';
          promoType = params.get('promo_type') || '';

          if (sessionId) setSessionID(sessionId);
          if (token) setTokenID(token);

        } catch {
          // The URL may contain id_token. Never attach the parser error because
          // browser implementations can include the full URL in its message.
          console.warn('[PartnerAuth] URL parser failed; using fallback parser');

          const matchToken = url.match(/[?&]id_token=([^&#]*)/);
          const matchSession = url.match(/[?&]session_id=([^&#]*)/);
          const matchPromoType = url.match(/[?&]promo_type=([^&#]*)/);

          if (matchSession) {
            sessionId = decodeURIComponent(matchSession[1]);
            setSessionID(sessionId);
          }

          if (matchToken) {
            token = decodeURIComponent(matchToken[1]);
            setTokenID(token);
          }

          if (matchPromoType) {
            promoType = decodeURIComponent(matchPromoType[1]);
          }
        }

        if (explicitSignOut) {
          // Recovered credentials belong to the session the user just
          // signed out of — treat them as consumed. No error modal: the
          // signed-out state is deliberate, not a failure.
          token = '';
          sessionId = '';
        }

        AsyncStorage.setItem('promoType', promoType).catch(() => {
          console.error('[PartnerAuth] promo type persistence failed');
        });

        if (token && sessionId) {
          // SMG-4069: if the live stream observed this player being unseated
          // from THIS session (simulator removal, idle eviction), the silent
          // auto-login must not run — its server-side JoinSession side effect
          // would re-seat a player who watched themselves get signed out at
          // the bay, and re-enroll them in autoplay, every time FullSwing's
          // host software reloads this page (~5-minute cadence in prod).
          // Rejoining requires a tap — the intent a background reload can
          // never produce. Storage failures fail open (gate reads unset).
          const gatedSessionId = sessionId;
          const gatedToken = token;
          const gatedPromoType = promoType;
          isRemovalGatedForSession(gatedSessionId).then(gated => {
            if (!gated) {
              fullSwingTokenLogin(gatedSessionId, gatedToken, gatedPromoType);
              return;
            }
            Context.setCancelModalConfig({
              visible: true, title: 'Signed Out at the Bay',
              subtitle: 'You were signed out of your bay session. Rejoin to keep playing on this bay.',
              leftBtnText: 'Not Now', rightBtnText: 'Rejoin',
              onExit: () => {
                // Deliberately stay signed out; the gate stays armed so the
                // next background reload stays silent too.
                Context.setCancelModalConfig({...cancelModalConfig, visible: false});
              },
              onResume: () => {
                Context.setCancelModalConfig({...cancelModalConfig, visible: false});
                // The gate is released ONLY by fullSwingTokenLogin's success
                // handler — released by the OUTCOME, not the attempt. Clearing
                // here first meant a failed rejoin (expired token, network
                // drop) left the gate disarmed, and the next background
                // reload silently re-seated the player: the exact loop this
                // gate closes, reachable from one tap (review finding).
                fullSwingTokenLogin(gatedSessionId, gatedToken, gatedPromoType);
              },
            });
          });

        } else if (token) {
          // SMG-3268: the deep link carried an id_token but no session_id, so
          // there is no FullSwing session to resolve. Calling FullSwingAuth
          // with an empty session_id returns a raw "[invalid_argument]
          // fullswing auth: session_id is required" (seen in the SMG-3266
          // stage report). Surface actionable guidance instead of the raw
          // server error and do not hit the server.
          Context.setCancelModalConfig({
            visible: true, title: 'No Active Session', subtitle: AUTH_ERROR_MESSAGES.noActiveSession,
            rightBtnText: 'OK',
            onResume: () => {
              Context.setCancelModalConfig({...cancelModalConfig, visible: false});
            },
          });

        } else {
          // If there is no token/session, check for referrer origin to return user to FullSwing
          AsyncStorage.getItem('referrerOrigin').then(refOrigin => {
            if (refOrigin) {
              AsyncStorage.removeItem('referrerOrigin').then(() => {
                // Redirect to FullSwing with force_sign_out action
                window.open(`${refOrigin}/?action_type=force_sign_out&redirect_url=${window.location.origin}/partner-auth`, '_self');
              });
            }
          });
        }
      }
    }).catch(() => {
      console.warn('[PartnerAuth] initial URL lookup failed');
      Context.setCancelModalConfig({
        visible: true, title: 'Error', subtitle: AUTH_ERROR_MESSAGES.authenticationFailed, rightBtnText: 'OK',
        onResume: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
        }
      });
    });
  }, []);

  // Dev-only debug badge: confirms FullSwing mode is active (this component
  // only renders when isFullSwing is true) and surfaces whatever session
  // was injected — useful when running embedded in burrito-golf-web's
  // Skillstrike-iframe panel, where the session arrives via URL hash.
  const sessionLine = session
    ? `session: ${session.userId}${user?.firstname ? ` (${user.firstname})` : ''}`
    : 'session: —';

  return (
    <P position="absolute" top={9} left={15} color="#989898">
      {`${Version}\n${smgEnvironment !== 'prod' ? smgEnvironment : ''}\n${sessionLine}`}
    </P>
  );
};

export default PartnerAuth;
